import { mapOverviewFishGroupSummaryToDomain } from '@/domain/fishGroups/mappers';
import { isHealthParameterKey } from '@/domain/health';
import type {
    CageHealthAlertOverviewDto,
    CageHealthOverviewDto,
    CageOverviewDataDto,
    CageOverviewDto,
    ClientOverviewResponseDto,
    LiceOverviewDto,
    LocationAveragesDto,
    LocationOverviewDto,
    SwimSpeedOverviewDto,
    WeightOverviewDto,
    WelfareOverviewDto,
} from '@/services/types';

import { allCagesOf } from './cageStatus';
import type {
    CageHealthAlertOverview,
    CageHealthOverview,
    CageOverviewData,
    ClientOverview,
    LiceOverview,
    LocationAverages,
    OverviewCage,
    OverviewLocation,
    SwimSpeedOverview,
    WeightOverview,
    WelfareOverview,
} from './types';

export function mapClientOverviewToDomain(response: ClientOverviewResponseDto): ClientOverview {
    const activeLocations = response.activeLocations.map(mapOverviewLocationToDomain);
    const inactiveLocations = response.inactiveLocations.map(mapOverviewLocationToDomain);
    return {
        clientId: response.clientId,
        activeLocations,
        inactiveLocations,
        locations: [...activeLocations, ...inactiveLocations],
    };
}

export function mapOverviewLocationToDomain(location: LocationOverviewDto): OverviewLocation {
    const buckets = {
        activeCages: location.activeCages.map(mapOverviewCageToDomain),
        inactiveCages: location.inactiveCages.map(mapOverviewCageToDomain),
        emptyCages: (location.emptyCages ?? []).map(mapOverviewCageToDomain),
        historicalCages: location.historicalCages.map(mapOverviewCageToDomain),
    };
    return {
        locationId: location.locationId,
        locationName: location.locationName,
        locationType: location.locationType,
        locationLiceType: location.locationLiceType,
        locationFishType: location.locationFishType ?? null,
        locationAverages: mapLocationAveragesToDomain(location.locationAverages),
        ...buckets,
        cages: allCagesOf(buckets),
        // Key presence, not truthiness: an empty array (asked, none found) is a
        // different answer from an absent field (did not ask), and only the second
        // should leave the property off.
        ...('closedFishGroups' in location
            ? { closedFishGroups: location.closedFishGroups ?? [] }
            : {}),
    };
}

export function mapOverviewCageToDomain(cage: CageOverviewDto): OverviewCage {
    return {
        cageId: cage.cageId,
        cageName: cage.cageName,
        cageType: cage.cageType,
        activeBioscopes: cage.activeBioscopes,
        fishType: cage.fishType ?? null,
        historicalSince: cage.historicalSince ?? null,
        bioscopeVersion: cage.bioscopeVersion,
        cageData: mapCageOverviewDataToDomain(cage.cageData),
        // Three states collapse into two here, deliberately. The generated DTO
        // types the field as optional and drops the `| null` the API actually
        // sends, so `'activeFishGroup' in cage` is what distinguishes "the request
        // did not ask" (leave undefined) from "asked, no cohort" (null).
        ...('activeFishGroup' in cage
            ? {
                  activeFishGroup: cage.activeFishGroup
                      ? mapOverviewFishGroupSummaryToDomain(cage.activeFishGroup)
                      : null,
              }
            : {}),
    };
}

function mapCageHealthOverviewToDomain(health: CageHealthOverviewDto): CageHealthOverview {
    return {
        day: health.day ?? null,
        downgradeRate: health.downgradeRate ?? null,
        woundRate: health.woundRate ?? null,
        woundSlope: health.woundSlope ?? null,
        alerts: health.alerts
            .map(mapCageHealthAlertToDomain)
            .filter((alert): alert is CageHealthAlertOverview => alert != null),
        topAlertParameterKey:
            health.topAlertParameterKey != null && isHealthParameterKey(health.topAlertParameterKey)
                ? health.topAlertParameterKey
                : undefined,
    };
}

function mapCageHealthAlertToDomain(
    alert: CageHealthAlertOverviewDto
): CageHealthAlertOverview | null {
    if (!isHealthParameterKey(alert.parameterKey)) {
        return null;
    }
    return {
        parameterKey: alert.parameterKey,
        level: alert.level,
        index: alert.index ?? null,
    };
}

function mapCageOverviewDataToDomain(data: CageOverviewDataDto): CageOverviewData {
    return {
        swimSpeed: data.swimSpeed ? mapSwimSpeedToDomain(data.swimSpeed) : undefined,
        weight: data.weight ? mapWeightToDomain(data.weight) : undefined,
        welfare: data.welfare ? mapWelfareToDomain(data.welfare) : undefined,
        lice: data.lice ? mapLiceToDomain(data.lice) : undefined,
        health: data.health ? mapCageHealthOverviewToDomain(data.health) : undefined,
    };
}

function mapSwimSpeedToDomain(speed: SwimSpeedOverviewDto): SwimSpeedOverview {
    return {
        swimSpeed: speed.swimSpeed,
        lastSpeedMeasurement: speed.lastSpeedMeasurement,
        daypartTag: speed.daypartTag ?? null,
    };
}

export function mapWelfareToDomain(welfare: WelfareOverviewDto): WelfareOverview {
    return {
        day: welfare.day,
        woundRate: welfare.woundRate,
        woundSlope: welfare.woundSlope,
        downgradeRate: welfare.downgradeRate,
    };
}

function mapLiceToDomain(lice: LiceOverviewDto): LiceOverview {
    return {
        day: lice.day,
        femaleLegacyAvg: lice.femaleLegacyAvg,
        femaleLegacyAvgDiff: lice.femaleLegacyAvgDiff,
    };
}

function mapWeightToDomain(weight: WeightOverviewDto): WeightOverview {
    return {
        day: weight.day,
        growth: weight.growth,
        livingWeight: weight.livingWeight,
        measStatusLevel: weight.measStatusLevel,
        measStatusList: weight.measStatusList,
        ackedStatusList: weight.ackedStatusList,
    };
}

function mapLocationAveragesToDomain(averages: LocationAveragesDto): LocationAverages {
    return {
        downgrade: averages.downgrade,
        femaleLice: averages.femaleLice,
        growth: averages.growth,
        wounds: averages.wounds,
    };
}
